import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Box,
  Button,
  Container,
  DialogActions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type {
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowModesModel,
} from '@mui/x-data-grid';
import { DataGrid, GridRowModes } from '@mui/x-data-grid';

import {
  type ChannelMarkupConditionApplyType,
  type ChannelMarkupConditionType,
  channelMarkupConditionConfig,
  currencyOptions,
  getChannelMarkupConditionConfigByKey,
  subscriberTierOptions,
  userRoleOptions,
} from '@luxuryescapes/contract-svc-promo/channelMarkup';
import type { components } from '@luxuryescapes/contract-svc-promo/generated';

import ChannelMarkupConditionValueEditCell from '~/components/ChannelMarkup/ChannelMarkupConditionValueEditCell';
import ChannelMarkupCtaTextEditCell from '~/components/ChannelMarkup/ChannelMarkupCtaTextEditCell';
import ChannelMarkupTesterPanel from '~/components/ChannelMarkup/ChannelMarkupTesterPanel';
import GlobalScopedMultiSelect from '~/components/Common/Elements/GlobalScopedMultiSelect';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import DebugModal from '~/components/DebugModal/DebugModal';

import { toPromoBrand } from '~/consts/promoBrand';
import { ROLE_CHANNEL_MARKUP_ADMIN_USER } from '~/consts/roles';

import { useChannelMarkupV2 } from '~/hooks/apis/promo/useChannelMarkupV2';
import { useCheckChannelMarkupV2 } from '~/hooks/apis/promo/useCheckChannelMarkupV2';
import { useGetActiveChannelMarkupV2List } from '~/hooks/apis/promo/useGetActiveChannelMarkupV2List';
import { useGetChannelMarkupV2ById } from '~/hooks/apis/promo/useGetChannelMarkupV2ById';
import { useGetClientInit } from '~/hooks/apis/promo/useGetClientInit';
import { useListAudiences } from '~/hooks/apis/promo/useListAudiences';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useCurrentUser from '~/hooks/useCurrentUser';
import useDebounced from '~/hooks/useDebounced';

import isProd from '~/utils/isProd';

type ChannelMarkupV2Internal = import('@luxuryescapes/contract-svc-promo/channelMarkup').ChannelMarkupV2Internal;
type ChannelMarkupCondition = import('@luxuryescapes/contract-svc-promo/channelMarkup').ChannelMarkupCondition;
type ChannelMarkupMarkup = import('@luxuryescapes/contract-svc-promo/channelMarkup').ChannelMarkupMarkup;
type ChannelMarkupV2ClientInitCondition =
  import('@luxuryescapes/contract-svc-promo/channelMarkup').ChannelMarkupV2ClientInitCondition;
type ChannelMarkupV2ClientInitMarkup =
  import('@luxuryescapes/contract-svc-promo/channelMarkup').ChannelMarkupV2ClientInitMarkup;
type ChannelMarkupV2ClientInitRules =
  import('@luxuryescapes/contract-svc-promo/channelMarkup').ChannelMarkupV2ClientInitRules;
type ChannelMarkupRulesCreateOrUpdate =
  import('@luxuryescapes/contract-svc-promo/generated').components['schemas']['channelMarkupRulesCreateOrUpdate'];
type ChannelMarkupRulesCondition = ChannelMarkupRulesCreateOrUpdate['conditions'][number];
type CreateChannelMarkupPayload =
  import('@luxuryescapes/contract-svc-promo/generated').operations['createChannelMarkupV2']['requestBody']['content']['application/json'];
type UpdateChannelMarkupPayload =
  import('@luxuryescapes/contract-svc-promo/generated').operations['updateChannelMarkupV2']['requestBody']['content']['application/json'];
type PromoConditionTypeConfig = (typeof channelMarkupConditionConfig)[number];
export const CHANNEL_MARKUP_CREATABLE_CONDITION_TYPES = [
  'loyaltyOfferType',
  'subscriberTier',
  'userRoleCondition',
] as const satisfies ReadonlyArray<PromoConditionTypeConfig['key']>;

const isCreatableChannelMarkupConditionType = (
  config: PromoConditionTypeConfig,
): config is (typeof channelMarkupConditionConfig)[number] & {
  key: (typeof CHANNEL_MARKUP_CREATABLE_CONDITION_TYPES)[number];
} =>
  CHANNEL_MARKUP_CREATABLE_CONDITION_TYPES.includes(
    config.key as (typeof CHANNEL_MARKUP_CREATABLE_CONDITION_TYPES)[number],
  );

const siteLocationConfig = [{ key: 'website', displayName: 'Website' }] as const;

type ChannelMarkupHydrationCondition = {
  conditionId?: string;
  conditionType: string;
  conditionApplyType: string;
  conditionValue: string;
  specificMarkupIds?: Array<string> | null;
};

type ChannelMarkupLocationState = {
  channelMarkup?: ChannelMarkupV2Internal;
};

/**
 * (moving to contract type too)
 */
type FormCondition = {
  id: string;
  condition_type: ChannelMarkupConditionType;
  condition_apply_type: ChannelMarkupCondition['conditionApplyType'];
  condition_value: ChannelMarkupCondition['conditionValue'];
  specificMarkupIds: Array<string>;
};

type ChannelMarkupFormMarkup = {
  id: string;
  markup_type: ChannelMarkupMarkup['markupType'];
  markup_value: string;
  lux_plus_markup_value: string;
  apply_priority: string;
  channelMarkupCTAText: string;
  currency: NonNullable<ChannelMarkupV2ClientInitMarkup['currency']> | '';
  shortKey?: string | null;
};

type FormValues = {
  conditions: Array<FormCondition>;
  markups: Array<ChannelMarkupFormMarkup>;
  expiresAt: string;
};

type TesterValues = {
  context: {
    region: string;
    currency: string;
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    utmTerm: string;
    subscriberTier: string;
    roles: Array<string>;
    offer: {
      offerId: string;
      loyaltyOfferType: string;
      total: string;
      luxPlusPrice: string;
    };
  };
};

type TesterMarkupMode = 'current' | 'general';
type ClientInitMode = 'default' | 'compt';

type AudienceOption = {
  audienceId: string;
  audienceName: string;
  audienceType?: string;
  syncModel?: string;
};

type TesterEvaluationValues = {
  context: import('@luxuryescapes/contract-svc-promo/channelMarkup').ChannelMarkupEvaluationContext;
};

function isChannelMarkupLocationState(value: unknown): value is ChannelMarkupLocationState {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return 'channelMarkup' in value;
}

type RowSaveState = 'saved';

type ChannelMarkupHydrationSource = {
  channelMarkupId?: string;
  expiresAt?: string | null;
  expires_at?: string | null;
  createdAt?: string;
  created_at?: string;
  version?: string;
  warnings?: Array<{ code: string; level: string; message: string }>;
  rules?: {
    conditions?: Array<ChannelMarkupHydrationCondition>;
    markups?: Array<{
      markupId?: string;
      markupType: ChannelMarkupFormMarkup['markup_type'];
      markupValue?: number | null;
      luxPlusMarkupValue?: number | null;
      applyPriority?: number | null;
      channelMarkupCTAText?: string | null;
      currency?: ChannelMarkupFormMarkup['currency'];
      shortKey?: string | null;
    }>;
  };
};

export const formatChannelMarkupConditionLabel = (markup: Pick<ChannelMarkupFormMarkup, 'markup_type' | 'shortKey'>) =>
  markup.shortKey?.trim() || markup.markup_type;

export const removeMarkupRow = (
  markups: Array<ChannelMarkupFormMarkup>,
  markupIdToRemove: string,
): Array<ChannelMarkupFormMarkup> => markups.filter((markup) => markup.id !== markupIdToRemove);

export const removeMarkupReferences = (
  conditions: Array<FormCondition>,
  markupIdToRemove: string,
): Array<FormCondition> =>
  conditions.map((condition) => ({
    ...condition,
    specificMarkupIds: condition.specificMarkupIds.filter((markupId) => markupId !== markupIdToRemove),
  }));

const defaultMarkup = (): ChannelMarkupFormMarkup => ({
  id: crypto.randomUUID(),
  markup_type: 'percentage',
  markup_value: '',
  lux_plus_markup_value: '',
  apply_priority: '',
  channelMarkupCTAText: '',
  currency: 'AUD',
});

const isChannelMarkupConditionType = (value: unknown): value is ChannelMarkupConditionType =>
  typeof value === 'string' && channelMarkupConditionConfig.some((config) => config.key === value);

const isChannelMarkupConditionApplyType = (value: unknown): value is ChannelMarkupConditionApplyType =>
  typeof value === 'string' &&
  channelMarkupConditionConfig.some((config) =>
    config.conditionApplyTypes?.some((conditionApplyType) => conditionApplyType === value),
  );

const isConditionApplyTypeForCondition = (
  value: unknown,
  conditionType: string,
): value is ChannelMarkupConditionApplyType =>
  typeof value === 'string' &&
  (getChannelMarkupConditionConfigByKey(conditionType as ChannelMarkupConditionType).conditionApplyTypes ?? []).some(
    (conditionApplyType) => conditionApplyType === value,
  );

const getDefaultConditionConfig = () => channelMarkupConditionConfig[0] ?? null;

const toNumberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getChannelMarkupSaveErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'errors' in error && Array.isArray(error.errors)) {
    const message = error.errors.find(
      (item): item is { message: string } =>
        typeof item === 'object' && item !== null && 'message' in item && typeof item.message === 'string',
    )?.message;

    if (message) {
      return message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
};

export const buildConditionPayload = (condition: FormCondition): ChannelMarkupRulesCondition | null => {
  const conditionConfig = isChannelMarkupConditionType(condition.condition_type)
    ? getChannelMarkupConditionConfigByKey(condition.condition_type)
    : null;

  if (!conditionConfig || !isCreatableChannelMarkupConditionType(conditionConfig)) {
    return null;
  }

  const fallbackApplyType = conditionConfig?.conditionApplyTypes?.[0] ?? 'MUST-INCLUDE';
  const conditionType = conditionConfig.key;
  const conditionApplyType = isChannelMarkupConditionApplyType(condition.condition_apply_type)
    ? condition.condition_apply_type
    : fallbackApplyType;

  return {
    conditionId: condition.id,
    specificMarkupIds: condition.specificMarkupIds.length > 0 ? condition.specificMarkupIds : null,
    conditionApplyType,
    conditionValue: condition.condition_value,
    conditionType,
  };
};

const buildChannelMarkupRulesPayload = (
  conditions: Array<FormCondition>,
  markups: Array<ChannelMarkupFormMarkup>,
): ChannelMarkupRulesCreateOrUpdate => {
  const conditionPayloads = conditions.flatMap((condition) => {
    const payload = buildConditionPayload(condition);
    return payload ? [payload] : [];
  });

  return {
    conditions: conditionPayloads,
    markups: markups.map((markup) => {
      const applyPriority = toNumberOrUndefined(markup.apply_priority);

      return {
        markupId: markup.id,
        markupType: markup.markup_type,
        markupValue: toNumberOrUndefined(markup.markup_value),
        luxPlusMarkupValue: toNumberOrUndefined(markup.lux_plus_markup_value),
        ...(applyPriority !== undefined ? { applyPriority } : {}),
        channelMarkupCTAText: markup.channelMarkupCTAText || null,
        ...(markup.currency ? { currency: markup.currency } : {}),
      };
    }),
  };
};

const buildCreateChannelMarkupPayload = (
  tenantBrand: components['schemas']['Promo Brands'],
  conditions: Array<FormCondition>,
  markups: Array<ChannelMarkupFormMarkup>,
  expiresAt: string,
) =>
  ({
    brand: tenantBrand,
    ...(expiresAt.trim() ? { expiresAt: dayjs(expiresAt).toISOString() } : {}),
    rules: buildChannelMarkupRulesPayload(conditions, markups),
  }) as import('@luxuryescapes/contract-svc-promo/generated').operations['createChannelMarkupV2']['requestBody']['content']['application/json'];

const buildUpdateChannelMarkupPayload = (
  channelMarkupId: string,
  tenantBrand: components['schemas']['Promo Brands'],
  conditions: Array<FormCondition>,
  markups: Array<ChannelMarkupFormMarkup>,
  expiresAt: string,
): UpdateChannelMarkupPayload => ({
  channelMarkupId,
  brand: tenantBrand,
  ...(expiresAt.trim() ? { expiresAt: dayjs(expiresAt).toISOString() } : {}),
  rules: buildChannelMarkupRulesPayload(conditions, markups),
});

const renderMarkupValue = (markupType: ChannelMarkupFormMarkup['markup_type'], value: string) => {
  const numericValue = Number(value);

  if (value.trim() === '' || (Number.isFinite(numericValue) && numericValue === 0)) {
    return '--';
  }

  return markupType === 'fixed' ? `$${value}` : `${value}%`;
};

const getConditionConfig = (conditionTypeConfigMap: Map<string, PromoConditionTypeConfig>, conditionType: string) =>
  conditionTypeConfigMap.get(conditionType);

const getConditionTypeLabel = (conditionTypeOptions: Array<{ label: string; value: string }>, conditionType: string) =>
  conditionTypeOptions.find((option) => option.value === conditionType)?.label ?? conditionType;

const renderColumnHeader = (label: string, description: string, showDetails: boolean) =>
  showDetails ? (
    <Stack spacing={0.25} sx={{ py: 0.5, whiteSpace: 'normal' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {description}
      </Typography>
    </Stack>
  ) : (
    label
  );

const conditionColumns = (
  markups: Array<ChannelMarkupFormMarkup>,
  conditionTypeOptions: Array<{ label: string; value: string }>,
  conditionTypeConfigMap: Map<string, PromoConditionTypeConfig>,
  onRemove: (id: string) => void,
  onUpdate: (id: string, patch: Partial<FormCondition>) => void,
  showDetails: boolean,
): Array<GridColDef<FormCondition>> => [
  {
    field: 'condition_type',
    headerName: 'Condition Type',
    renderHeader: () =>
      renderColumnHeader('Condition Type', 'What is evaluated to decide whether this condition matches.', showDetails),
    flex: 1,
    renderCell: (params) => {
      const conditionType = params.row.condition_type;
      const conditionConfig = getConditionConfig(conditionTypeConfigMap, conditionType);

      return showDetails && conditionConfig?.longDescription ? (
        <Stack spacing={0.25}>
          <Typography sx={{ fontWeight: 600 }}>{getConditionTypeLabel(conditionTypeOptions, conditionType)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {conditionConfig.longDescription}
          </Typography>
        </Stack>
      ) : (
        <Typography sx={{ fontWeight: 600 }}>{getConditionTypeLabel(conditionTypeOptions, conditionType)}</Typography>
      );
    },
    renderEditCell: (params) => (
      <Stack spacing={0.5}>
        <TextField
          select
          autoFocus
          fullWidth
          value={params.value ?? ''}
          onChange={(event) => {
            const conditionType = event.target.value;
            const nextConfig = getConditionConfig(conditionTypeConfigMap, conditionType);
            const nextApplyType =
              nextConfig?.conditionApplyTypes?.[0] ?? (params.row.condition_apply_type || 'MUST-INCLUDE');
            params.api.setEditCellValue({ id: params.id, field: params.field, value: conditionType });
            onUpdate(String(params.id), {
              condition_type: conditionType as ChannelMarkupConditionType,
              condition_apply_type: nextApplyType,
              condition_value: '',
            });
            params.api.setEditCellValue({
              id: params.id,
              field: 'condition_apply_type',
              value: nextApplyType,
            });
            params.api.setEditCellValue({ id: params.id, field: 'condition_value', value: '' });
          }}
          helperText={showDetails ? 'Select what kind of condition this rule uses.' : undefined}
        >
          {conditionTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {getConditionConfig(conditionTypeConfigMap, params.row.condition_type)?.longDescription ? (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {getConditionConfig(conditionTypeConfigMap, params.row.condition_type)?.longDescription}
          </Typography>
        ) : null}
      </Stack>
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'condition_apply_type',
    headerName: 'Apply Type',
    renderHeader: () => renderColumnHeader('Apply Type', 'How the selected condition value must match.', showDetails),
    flex: 1,
    renderEditCell: (params) => {
      const conditionType = params.row.condition_type;
      const conditionConfig = getConditionConfig(conditionTypeConfigMap, conditionType);
      const conditionApplyTypes = conditionConfig?.conditionApplyTypes ?? [];

      if (conditionApplyTypes.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            --
          </Typography>
        );
      }

      return (
        <TextField
          select
          autoFocus
          fullWidth
          value={params.value ?? ''}
          onChange={(event) => {
            const conditionApplyType = isConditionApplyTypeForCondition(event.target.value, conditionType)
              ? event.target.value
              : (conditionApplyTypes[0] ?? 'MUST-INCLUDE');
            params.api.setEditCellValue({ id: params.id, field: params.field, value: conditionApplyType });
            onUpdate(String(params.id), {
              condition_apply_type: conditionApplyType,
            });
          }}
          helperText={showDetails ? `Use one of the apply types configured for ${conditionType}.` : undefined}
        >
          {conditionApplyTypes.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      );
    },
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'condition_value',
    headerName: 'Condition Value',
    renderHeader: () =>
      renderColumnHeader('Condition Value', 'The value tested by the selected condition type.', showDetails),
    flex: 1.2,
    renderEditCell: (params) => {
      const config = getConditionConfig(conditionTypeConfigMap, params.row.condition_type);
      return (
        <ChannelMarkupConditionValueEditCell
          id={params.id}
          field={params.field}
          value={params.value}
          isDatetime={false}
          suggestedConditionValues={[...(config?.suggestedConditionValues ?? [])]}
        />
      );
    },
    preProcessEditCellProps: (params) => ({
      ...params.props,
      error: String(params.props.value ?? '').trim() === '',
    }),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'specificMarkupIds',
    headerName: 'Specific Markups',
    renderHeader: () =>
      renderColumnHeader(
        'Specific Markups',
        'Limit this condition to selected markups; leave empty to apply it to all markups.',
        showDetails,
      ),
    flex: 1.3,
    renderCell: (params: GridRenderCellParams<FormCondition, Array<string>>) => {
      const selectedMarkupIds = params.row.specificMarkupIds;

      if (!selectedMarkupIds.length) {
        return <Typography sx={{ fontWeight: 600 }}>Applies to all markups</Typography>;
      }

      return selectedMarkupIds.join(', ');
    },
    renderEditCell: (params: GridRenderEditCellParams<FormCondition, Array<string>>) => {
      const selectedMarkupIds = Array.isArray(params.value) ? params.value : [];

      return (
        <GlobalScopedMultiSelect
          value={selectedMarkupIds}
          globalLabel="Applies to all markups"
          hideWhenGlobal
          options={markups.map((markup) => ({
            value: markup.id,
            label: formatChannelMarkupConditionLabel(markup),
          }))}
          onChange={(specificMarkupIds) => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: specificMarkupIds });
            onUpdate(String(params.id), { specificMarkupIds });
          }}
        />
      );
    },
    editable: true,
    sortable: false,
    filterable: false,
    width: 260,
  },
  {
    field: 'debug',
    headerName: 'Debug',
    flex: 1.4,
    renderCell: (params) =>
      showDetails ? (
        <Typography
          component="pre"
          variant="caption"
          sx={{
            m: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
          }}
        >
          {JSON.stringify(params.row, null, 2)}
        </Typography>
      ) : null,
    editable: false,
    sortable: false,
    filterable: false,
  },
];

const markupColumns = (showDetails: boolean): Array<GridColDef<ChannelMarkupFormMarkup>> => [
  {
    field: 'markup_type',
    headerName: 'Markup Type',
    renderHeader: () =>
      renderColumnHeader(
        'Markup Type',
        'Percentage adjusts the price relatively; fixed adds a currency amount.',
        showDetails,
      ),
    flex: 1,
    renderEditCell: (params) => (
      <TextField
        select
        autoFocus
        fullWidth
        value={params.row.markup_type}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: 'markup_type', value: event.target.value })
        }
        helperText={
          showDetails ? 'Choose percentage for a relative adjustment or fixed for an absolute amount.' : undefined
        }
      >
        <MenuItem value="percentage">percentage</MenuItem>
        <MenuItem value="fixed">fixed</MenuItem>
      </TextField>
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'markup_value',
    headerName: 'Normal Price Markup',
    renderHeader: () =>
      renderColumnHeader('Normal Price Markup', 'Adjustment applied to standard, non-Lux Plus prices.', showDetails),
    flex: 1,
    renderCell: (params) => renderMarkupValue(params.row.markup_type, params.row.markup_value),
    renderEditCell: (params) => (
      <TextField
        autoFocus
        fullWidth
        value={params.row.markup_value}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: 'markup_value', value: event.target.value })
        }
        helperText={showDetails ? 'Enter the markup for normal prices.' : undefined}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'lux_plus_markup_value',
    headerName: 'Lux Plus Markup',
    renderHeader: () => renderColumnHeader('Lux Plus Markup', 'Adjustment applied to Lux Plus prices.', showDetails),
    flex: 1,
    renderCell: (params) => renderMarkupValue(params.row.markup_type, params.row.lux_plus_markup_value),
    renderEditCell: (params) => (
      <TextField
        autoFocus
        fullWidth
        value={params.row.lux_plus_markup_value}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: 'lux_plus_markup_value', value: event.target.value })
        }
        helperText={showDetails ? 'Enter the Lux Plus-specific markup.' : undefined}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'apply_priority',
    headerName: 'Apply Priority',
    renderHeader: () =>
      renderColumnHeader('Apply Priority', 'Higher values take precedence when multiple markups match.', showDetails),
    flex: 0.8,
    renderCell: (params) => params.row.apply_priority || '--',
    renderEditCell: (params) => (
      <TextField
        autoFocus
        fullWidth
        type="number"
        value={params.row.apply_priority}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: 'apply_priority', value: event.target.value })
        }
        helperText={showDetails ? 'Higher values take precedence when multiple markups match.' : undefined}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'shortKey',
    headerName: 'Short Key',
    renderHeader: () =>
      renderColumnHeader('Short Key', 'Generated identifier used to reference this markup in conditions.', showDetails),
    flex: 0.8,
    renderCell: (params) => params.row.shortKey || '--',
    editable: false,
    sortable: false,
    filterable: false,
  },
  {
    field: 'channelMarkupCTAText',
    headerName: 'CTA Text',
    renderHeader: () =>
      renderColumnHeader('CTA Text', 'Optional customer-facing call to action for this markup.', showDetails),
    flex: 1,
    renderEditCell: (params) => <ChannelMarkupCtaTextEditCell params={params} showDetails={showDetails} />,
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'currency',
    headerName: 'Currency',
    renderHeader: () => renderColumnHeader('Currency', 'Currency for a fixed markup; AUD is the default.', showDetails),
    flex: 0.8,
    renderCell: (params) => <Typography sx={{ fontWeight: 600 }}>{params.row.currency || 'AUD'}</Typography>,
    renderEditCell: (params) => (
      <FormControl fullWidth>
        <InputLabel id={`currency-${params.id}-label`}>Currency</InputLabel>
        <Select
          labelId={`currency-${params.id}-label`}
          label="Currency"
          value={params.row.currency || 'AUD'}
          onChange={(event: SelectChangeEvent<string>) => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value });
          }}
        >
          {currencyOptions.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.displayName}
            </MenuItem>
          ))}
        </Select>
        {showDetails && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Select the currency scope for this markup.
          </Typography>
        )}
      </FormControl>
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'debug',
    headerName: 'Debug',
    flex: 1.4,
    renderCell: (params) =>
      showDetails ? (
        <Typography
          component="pre"
          variant="caption"
          sx={{
            m: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
          }}
        >
          {JSON.stringify(params.row, null, 2)}
        </Typography>
      ) : null,
    editable: false,
    sortable: false,
    filterable: false,
  },
];

export default function ChannelMarkupCreateV2Page() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useCurrentUser();
  const tenant = useCurrentTenant();
  const { evaluateChannelMarkup, applyChannelMarkup } = useChannelMarkupV2();
  const { goBack } = useAppNavigation();
  const { channelMarkupId } = useParams<{ channelMarkupId?: string }>();
  const location = useLocation();
  const initialChannelMarkup = isChannelMarkupLocationState(location.state) ? location.state.channelMarkup : undefined;
  const isEditMode = Boolean(channelMarkupId);
  const {
    data: serverChannelMarkup,
    isError: isChannelMarkupFetchError,
    isLoading: isChannelMarkupLoading,
  } = useGetChannelMarkupV2ById({ channelMarkupId, enabled: isEditMode });
  const displayedChannelMarkup = serverChannelMarkup ?? initialChannelMarkup;
  const draftChannelMarkupIdRef = useRef<string>(crypto.randomUUID());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [saveWarnings, setSaveWarnings] = useState<Array<string>>([]);
  const [clientInitRefreshMessage, setClientInitRefreshMessage] = useState('');
  const [clientInitRefreshError, setClientInitRefreshError] = useState('');
  const [testerCheckVersion, setTesterCheckVersion] = useState(0);
  const [testerMarkupMode, setTesterMarkupMode] = useState<TesterMarkupMode>('current');
  const [clientInitMode, setClientInitMode] = useState<ClientInitMode>('default');
  const [selectedCurrencies, setSelectedCurrencies] = useState<Array<string>>(['AUD']);
  const [conditionRowModesModel, setConditionRowModesModel] = useState<GridRowModesModel>({});
  const [markupRowModesModel, setMarkupRowModesModel] = useState<GridRowModesModel>({});
  const [conditionRowSaveState, setConditionRowSaveState] = useState<Record<string, RowSaveState>>({});
  const [markupRowSaveState, setMarkupRowSaveState] = useState<Record<string, RowSaveState>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [unsupportedConditionCount, setUnsupportedConditionCount] = useState(0);
  const {
    register: registerTester,
    watch: watchTester,
    setValue: setTesterValue,
  } = useForm<TesterValues>({
    defaultValues: {
      context: {
        region: 'AU',
        currency: 'AUD',
        utmSource: '',
        utmMedium: '',
        utmCampaign: '',
        utmTerm: '',
        subscriberTier: '',
        roles: [],
        offer: {
          offerId: '',
          loyaltyOfferType: 'HOTEL_FLASH',
          total: '',
          luxPlusPrice: '',
        },
      },
    },
  });

  const { handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      conditions: [],
      markups: [],
      expiresAt: '',
    },
  });
  const conditionTypeOptions = useMemo(
    () =>
      channelMarkupConditionConfig.filter(isCreatableChannelMarkupConditionType).map((config) => ({
        label: config.name,
        value: config.key,
      })),
    [],
  );
  const conditionTypeConfigMap = useMemo(
    () => new Map(channelMarkupConditionConfig.map((config) => [config.key, config])),
    [],
  );
  const createDefaultCondition = useCallback((): FormCondition | null => {
    const defaultConfig = channelMarkupConditionConfig.find(isCreatableChannelMarkupConditionType);

    if (!defaultConfig) {
      return null;
    }

    return {
      id: crypto.randomUUID(),
      condition_type: defaultConfig.key,
      condition_apply_type: defaultConfig.conditionApplyTypes?.[0] ?? 'MUST-INCLUDE',
      condition_value: '',
      specificMarkupIds: [],
    };
  }, []);
  const [conditions, setConditions] = useState<Array<FormCondition>>([]);
  const [markups, setMarkups] = useState<Array<ChannelMarkupFormMarkup>>([defaultMarkup()]);
  const [expiresAt, setExpiresAt] = useState<string>('');
  const tenantBrand = useMemo(() => toPromoBrand(tenant.tenant.brand) ?? 'luxuryescapes', [tenant.tenant.brand]);
  const { data: activeChannelMarkupData, isLoading: isActiveChannelMarkupsLoading } = useGetActiveChannelMarkupV2List({
    brand: tenantBrand,
    enabled: testerMarkupMode === 'general',
  });
  const hasWarnedAboutTenantBrandRef = useRef(false);

  useEffect(() => {
    if (hasWarnedAboutTenantBrandRef.current) {
      return;
    }

    if (!toPromoBrand(tenant.tenant.brand)) {
      hasWarnedAboutTenantBrandRef.current = true;
      enqueueSnackbar(
        `Tenant brand "${tenant.tenant.brand}" is not configured for the channel markup editor. Defaulting to luxuryescapes.`,
        { variant: 'warning' },
      );
    }
  }, [enqueueSnackbar, tenant.tenant.brand]);

  const createPayload = useMemo(
    () => buildCreateChannelMarkupPayload(tenantBrand, conditions, markups, expiresAt),
    [conditions, expiresAt, markups, tenantBrand],
  );

  const updatePayload = useMemo(
    () => buildUpdateChannelMarkupPayload(draftChannelMarkupIdRef.current, tenantBrand, conditions, markups, expiresAt),
    [conditions, expiresAt, markups, tenantBrand],
  );

  // Live server-side validation of the draft markup, driving canSave + warnings.
  const hasCheckInputs = conditions.length > 0 && markups.length > 0;
  const checkPayload = useMemo(
    () => ({
      channelMarkup: isEditMode ? { ...updatePayload, brand: updatePayload.brand ?? tenantBrand } : createPayload,
    }),
    [createPayload, isEditMode, tenantBrand, updatePayload],
  );
  // Debounce so rapid edits don't fire a check request per keystroke.
  const debouncedCheckPayload = useDebounced(checkPayload, 300);
  const {
    data: checkResult,
    isFetching: isChecking,
    error: checkError,
    refetch: refetchCheck,
  } = useCheckChannelMarkupV2({ payload: debouncedCheckPayload, enabled: hasCheckInputs });
  const canSave = hasCheckInputs && Boolean(checkResult?.canSave);
  // keepPreviousData would otherwise retain the last check's warnings after all rows are removed.
  const checkWarnings = useMemo(
    () => (hasCheckInputs ? (checkResult?.warnings ?? []) : []),
    [hasCheckInputs, checkResult?.warnings],
  );

  const conditionGridColumns = useMemo(
    () =>
      conditionColumns(
        markups,
        conditionTypeOptions,
        conditionTypeConfigMap,
        (id) => setConditions((current) => current.filter((row) => row.id !== id)),
        (id, patch) => setConditions((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row))),
        showDetails,
      ),
    [conditionTypeConfigMap, conditionTypeOptions, markups, showDetails],
  );

  const markupGridColumns = useMemo(() => markupColumns(showDetails), [showDetails]);
  const activeChannelMarkups = activeChannelMarkupData?.channelMarkupList ?? [];
  const testerValues = watchTester();
  const [audienceSearchInput, setAudienceSearchInput] = useState('');
  const debouncedAudienceSearchInput = useDebounced(audienceSearchInput.trim(), 300);
  const audienceSearchQuery = useMemo(
    () => ({
      page: 0,
      limit: 10,
      search: debouncedAudienceSearchInput || undefined,
    }),
    [debouncedAudienceSearchInput],
  );
  const audienceSearchEnabled = debouncedAudienceSearchInput.length >= 2;
  const { data: audienceSearchData, isFetching: isAudienceSearchLoading } = useListAudiences({
    query: audienceSearchQuery,
    enabled: audienceSearchEnabled,
  });
  const audienceOptions = useMemo<Array<AudienceOption>>(
    () =>
      audienceSearchData?.result?.audiences?.flatMap((audience) => {
        if (typeof audience !== 'object' || audience === null) {
          return [];
        }

        const rawAudience: {
          audienceId?: unknown;
          id?: unknown;
          audienceName?: unknown;
          name?: unknown;
          audienceType?: unknown;
          syncModel?: unknown;
        } = audience;

        return [
          {
            audienceId: String(rawAudience.audienceId ?? rawAudience.id ?? ''),
            audienceName: String(rawAudience.audienceName ?? rawAudience.name ?? ''),
            audienceType: typeof rawAudience.audienceType === 'string' ? rawAudience.audienceType : undefined,
            syncModel: typeof rawAudience.syncModel === 'string' ? rawAudience.syncModel : undefined,
          },
        ];
      }) ?? [],
    [audienceSearchData?.result?.audiences],
  );
  const noAudienceResults = audienceSearchEnabled && !isAudienceSearchLoading && audienceOptions.length === 0;
  const effectiveTesterContext = useMemo<TesterEvaluationValues['context']>(
    () => ({
      region: testerValues.context.region.trim() || 'AU',
      currency: testerValues.context.currency.trim() || 'AUD',
      utmSource: testerValues.context.utmSource.trim() || null,
      utmMedium: testerValues.context.utmMedium.trim() || null,
      utmCampaign: testerValues.context.utmCampaign.trim() || null,
      utmTerm: testerValues.context.utmTerm.trim() || null,
      subscriberTier: testerValues.context.subscriberTier.trim() || null,
      roles: testerValues.context.roles,
      offer: {
        version: 'cmOfferV1',
        total: Number(testerValues.context.offer.total.trim() || 0),
        luxPlusPrice:
          testerValues.context.offer.luxPlusPrice.trim() === ''
            ? null
            : Number(testerValues.context.offer.luxPlusPrice),
        offerId: testerValues.context.offer.offerId.trim() || 'test-offer',
        loyaltyOfferType: testerValues.context.offer.loyaltyOfferType.trim() || 'HOTEL_FLASH',
      },
    }),
    [testerValues],
  );
  const clientInitQuery = useMemo(
    () => ({
      region: effectiveTesterContext.region,
      currency: effectiveTesterContext.currency,
      brand: tenantBrand,
      isTestEnviroment: !isProd(),
      requestClient: 'customer-portal',
      mode: clientInitMode,
    }),
    [clientInitMode, effectiveTesterContext.currency, effectiveTesterContext.region, tenantBrand],
  );
  const {
    data: clientInitConfig,
    refetch: refetchClientInit,
    dataUpdatedAt: clientInitUpdatedAt,
    isFetching: isClientInitFetching,
  } = useGetClientInit({
    query: clientInitQuery,
    enabled: testerMarkupMode === 'general',
  });
  const canEditMarkup = Boolean(user?.roles?.includes(ROLE_CHANNEL_MARKUP_ADMIN_USER));
  const canEditRows = canEditMarkup;
  const isClientInitReady = Boolean(clientInitConfig);
  const canRunGeneralTesterMode = testerMarkupMode === 'general' && isClientInitReady;
  const effectiveTesterMarkupMode: TesterMarkupMode = canRunGeneralTesterMode ? 'general' : 'current';
  useEffect(() => {
    if (testerMarkupMode !== 'general' || isClientInitReady || isClientInitFetching) {
      return;
    }

    void refetchClientInit();
  }, [isClientInitFetching, isClientInitReady, refetchClientInit, testerMarkupMode]);
  const handleRunPreview = useCallback(() => {
    setTesterCheckVersion((current) => current + 1);
  }, []);

  const debugState = useMemo(
    () => ({
      row: {
        isEditMode,
        isSubmitting,
        showDetails,
        hasUnsavedChanges,
        testerMarkupMode,
        draftChannelMarkupId: draftChannelMarkupIdRef.current,
        selectedCurrencies,
        testerValues,
        conditionRowModesModel,
        markupRowModesModel,
        conditionRowSaveState,
        markupRowSaveState,
        conditions,
        markups,
        expiresAt,
        createPayload,
        updatePayload,
      },
      conditionConfig: {
        conditionTypeOptions,
        subscriberTierOptions,
        userRoleOptions,
        currencyOptions,
      },
      ...(saveWarnings.length > 0 || checkWarnings.length > 0
        ? {
            errorsWarnings: {
              errors: [],
              warnings: [...checkWarnings, ...saveWarnings],
            },
          }
        : {}),
    }),
    [
      conditionRowModesModel,
      conditionRowSaveState,
      conditions,
      createPayload,
      hasUnsavedChanges,
      isEditMode,
      conditionTypeOptions,
      isSubmitting,
      markupRowModesModel,
      markupRowSaveState,
      markups,
      selectedCurrencies,
      showDetails,
      testerValues,
      testerMarkupMode,
      updatePayload,
      expiresAt,
      saveWarnings,
      checkWarnings,
    ],
  );
  const testerResults = useMemo(() => {
    if (testerCheckVersion === 0) {
      return [];
    }

    return selectedCurrencies.map((currency) =>
      evaluateChannelMarkup({
        mode: effectiveTesterMarkupMode,
        channelMarkupId: draftChannelMarkupIdRef.current,
        conditions,
        markups,
        testerValues: {
          context: {
            ...effectiveTesterContext,
            currency,
          },
        },
        clientInitConfig: effectiveTesterMarkupMode === 'general' ? clientInitConfig : undefined,
      }),
    );
  }, [
    conditions,
    effectiveTesterContext,
    evaluateChannelMarkup,
    clientInitConfig,
    markups,
    selectedCurrencies,
    tenant.tenant.brand,
    testerCheckVersion,
    effectiveTesterMarkupMode,
  ]);
  const testerResult = useMemo(() => {
    if (testerResults.some((result) => result.status === 'matched')) {
      return 'matched';
    }
    if (testerResults.some((result) => result.status === 'pending')) {
      return 'pending';
    }
    return 'no-match';
  }, [testerResults]);

  useEffect(() => {
    if (conditionTypeOptions.length === 0 || conditions.length > 0) {
      return;
    }

    const defaultCondition = createDefaultCondition();
    if (defaultCondition) {
      setConditions([defaultCondition]);
    }
  }, [conditionTypeOptions.length, conditions.length, createDefaultCondition]);

  const markConditionSaved = useCallback((id: string) => {
    setConditionRowSaveState((current) => ({ ...current, [id]: 'saved' }));
    window.setTimeout(() => {
      setConditionRowSaveState((current) => {
        if (current[id] !== 'saved') {
          return current;
        }

        const next = { ...current };
        delete next[id];
        return next;
      });
    }, 1200);
  }, []);

  const markMarkupSaved = useCallback((id: string) => {
    setMarkupRowSaveState((current) => ({ ...current, [id]: 'saved' }));
    window.setTimeout(() => {
      setMarkupRowSaveState((current) => {
        if (current[id] !== 'saved') {
          return current;
        }

        const next = { ...current };
        delete next[id];
        return next;
      });
    }, 1200);
  }, []);

  const handleRowEditStop = useCallback<GridEventListener<'rowEditStop'>>((params, event) => {
    if (params.reason === 'rowFocusOut') {
      event.defaultMuiPrevented = true;
    }
  }, []);

  const conditionGridWithActions = useMemo<Array<GridColDef<FormCondition>>>(() => {
    return [
      ...conditionGridColumns,
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 120,
        renderCell: (params) => {
          const isEditing = conditionRowModesModel[params.id]?.mode === GridRowModes.Edit;
          return (
            <Stack direction="row" spacing={0.5}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="Save condition"
                    size="small"
                    onClick={() => params.api.stopRowEditMode({ id: params.id })}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel condition edit"
                    size="small"
                    onClick={() => params.api.stopRowEditMode({ id: params.id, ignoreModifications: true })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Remove condition"
                    size="small"
                    onClick={() => {
                      params.api.stopRowEditMode({ id: params.id, ignoreModifications: true });
                      setConditions((current) => current.filter((row) => row.id !== params.id));
                      setConditionRowModesModel((current) => {
                        const next = { ...current };
                        delete next[params.id];
                        return next;
                      });
                      setConditionRowSaveState((current) => {
                        if (!(params.id in current)) {
                          return current;
                        }

                        const next = { ...current };
                        delete next[params.id];
                        return next;
                      });
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {conditionRowSaveState[String(params.id)] === 'saved' && (
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                      Saved
                    </Typography>
                  )}
                  <IconButton
                    aria-label="Edit condition"
                    size="small"
                    onClick={() =>
                      setConditionRowModesModel((current) => ({
                        ...current,
                        [params.id]: { mode: GridRowModes.Edit },
                      }))
                    }
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          );
        },
      },
    ];
  }, [conditionGridColumns, conditionRowModesModel, conditionRowSaveState]);

  const markupGridWithActions = useMemo<Array<GridColDef<ChannelMarkupFormMarkup>>>(() => {
    return [
      ...markupGridColumns,
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        width: 132,
        renderCell: (params) => {
          const isEditing = markupRowModesModel[params.id]?.mode === GridRowModes.Edit;
          return (
            <Stack direction="row" spacing={0.5}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="Save markup"
                    size="small"
                    onClick={() => params.api.stopRowEditMode({ id: params.id })}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel markup edit"
                    size="small"
                    onClick={() => params.api.stopRowEditMode({ id: params.id, ignoreModifications: true })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Remove markup"
                    size="small"
                    onClick={() => {
                      params.api.stopRowEditMode({ id: params.id, ignoreModifications: true });
                      setMarkups((current) => current.filter((row) => row.id !== params.id));
                      setMarkupRowModesModel((current) => {
                        const next = { ...current };
                        delete next[params.id];
                        return next;
                      });
                      setMarkupRowSaveState((current) => {
                        if (!(params.id in current)) {
                          return current;
                        }

                        const next = { ...current };
                        delete next[params.id];
                        return next;
                      });
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {markupRowSaveState[String(params.id)] === 'saved' && (
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                      Saved
                    </Typography>
                  )}
                  <IconButton
                    aria-label="Edit markup"
                    size="small"
                    onClick={() =>
                      setMarkupRowModesModel((current) => ({
                        ...current,
                        [params.id]: { mode: GridRowModes.Edit },
                      }))
                    }
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          );
        },
      },
    ];
  }, [markupGridColumns, markupRowModesModel, markupRowSaveState]);

  // Surface check failures to the user.
  useEffect(() => {
    if (checkError) {
      enqueueSnackbar(`Failed to validate channel markup: ${checkError}`, { variant: 'error' });
    }
  }, [checkError, enqueueSnackbar]);

  const handleRefreshCheck = useCallback(() => {
    void refetchCheck();
  }, [refetchCheck]);

  const handleRefreshClientInit = useCallback(async () => {
    const startedAt = performance.now();
    setClientInitRefreshError('');

    try {
      const response = await refetchClientInit();
      const elapsedMs = Math.max(0, Math.round(performance.now() - startedAt));
      const config = response.data?.channelMarkupConfig;
      const v1Count = config?.channelMarkupListV1?.length ?? 0;
      const v2Count = config?.channelMarkupListV2?.length ?? 0;

      setClientInitRefreshMessage(
        `got ${v1Count} v1 channel markup and ${v2Count} v2 channel markup in ${elapsedMs} ms`,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setClientInitRefreshMessage('');
      setClientInitRefreshError(`Failed to load client init: ${message}`);
      enqueueSnackbar(`Failed to load client init: ${message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar, refetchClientInit]);

  const hydrateFormFromChannelMarkup = useCallback(
    (channelMarkup: ChannelMarkupHydrationSource) => {
      const rawConditions = channelMarkup.rules?.conditions ?? [];
      const initialConditions: Array<FormCondition> = rawConditions.flatMap((condition) => {
        const conditionType = isChannelMarkupConditionType(condition.conditionType)
          ? condition.conditionType
          : channelMarkupConditionConfig.find(isCreatableChannelMarkupConditionType)?.key;

        if (!conditionType) {
          return [];
        }

        return [
          {
            id: condition.conditionId ?? crypto.randomUUID(),
            condition_type: conditionType,
            condition_apply_type: isChannelMarkupConditionApplyType(condition.conditionApplyType)
              ? condition.conditionApplyType
              : (channelMarkupConditionConfig[0]?.conditionApplyTypes?.[0] ?? 'MUST-INCLUDE'),
            condition_value: condition.conditionValue,
            specificMarkupIds: condition.specificMarkupIds ?? [],
          },
        ];
      });
      const initialMarkups = channelMarkup.rules?.markups?.map((markup) => ({
        id: markup.markupId ?? crypto.randomUUID(),
        markup_type: markup.markupType,
        markup_value: markup.markupValue?.toString() ?? '',
        lux_plus_markup_value: markup.luxPlusMarkupValue?.toString() ?? '',
        apply_priority: markup.applyPriority?.toString() ?? '',
        channelMarkupCTAText: markup.channelMarkupCTAText ?? '',
        currency: markup.currency ?? 'AUD',
        shortKey: markup.shortKey,
      })) ?? [defaultMarkup()];
      const rawExpiresAt = channelMarkup.expiresAt ?? channelMarkup.expires_at;
      const initialExpiresAt = rawExpiresAt
        ? dayjs(rawExpiresAt).isValid()
          ? dayjs(rawExpiresAt).format('YYYY-MM-DDTHH:mm')
          : ''
        : '';
      const unsupportedConditions = rawConditions.filter(
        (condition) =>
          !isChannelMarkupConditionType(condition.conditionType) ||
          !isChannelMarkupConditionApplyType(condition.conditionApplyType),
      );

      const nextChannelMarkupId = channelMarkup.channelMarkupId ?? channelMarkupId ?? draftChannelMarkupIdRef.current;
      draftChannelMarkupIdRef.current = String(nextChannelMarkupId);

      if (unsupportedConditions.length > 0) {
        setUnsupportedConditionCount(unsupportedConditions.length);
        enqueueSnackbar(
          `Loaded channel markup contains ${unsupportedConditions.length} unsupported condition rule${
            unsupportedConditions.length === 1 ? '' : 's'
          }. This draft cannot be saved until those rules are removed or replaced in the source data.`,
          { variant: 'warning' },
        );
      } else {
        setUnsupportedConditionCount(0);
      }

      setConditions(initialConditions);
      setMarkups(initialMarkups.length > 0 ? initialMarkups : [defaultMarkup()]);
      setExpiresAt(initialExpiresAt);
      setHasUnsavedChanges(false);
      setConditionRowSaveState({});
      setMarkupRowSaveState({});
    },
    [channelMarkupId, createDefaultCondition, enqueueSnackbar],
  );

  const handleCreate = handleSubmit(async () => {
    if (!canSave || isChecking || !canEditRows || unsupportedConditionCount > 0) {
      return;
    }

    setIsSubmitting(true);
    setSaveWarnings([]);
    try {
      const { channelMarkup, warnings } = await applyChannelMarkup({
        isEditMode,
        createPayload,
        updatePayload,
      });

      setSaveWarnings(warnings);
      warnings.forEach((warning) => enqueueSnackbar(warning, { variant: 'warning' }));

      reset();
      enqueueSnackbar(`Channel Markup v2 ${isEditMode ? 'updated' : 'created'} successfully`, {
        variant: 'success',
      });
      if (!isEditMode) {
        setConditions([createDefaultCondition()]);
        setMarkups([defaultMarkup()]);
        setExpiresAt('');
        setHasUnsavedChanges(false);
        setConditionRowSaveState({});
        setMarkupRowSaveState({});
        return;
      }

      if (channelMarkup) {
        hydrateFormFromChannelMarkup(channelMarkup);
      }
    } catch (error) {
      enqueueSnackbar(
        `Channel Markup v2 ${isEditMode ? 'update' : 'creation'} failed: ${getChannelMarkupSaveErrorMessage(error)}`,
        {
          variant: 'error',
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  });

  useEffect(() => {
    const channelMarkup = serverChannelMarkup ?? initialChannelMarkup;

    if (!channelMarkup || (isEditMode && isChannelMarkupLoading)) {
      return;
    }
    hydrateFormFromChannelMarkup(channelMarkup);
  }, [
    createDefaultCondition,
    hydrateFormFromChannelMarkup,
    initialChannelMarkup,
    isChannelMarkupLoading,
    isEditMode,
    serverChannelMarkup,
  ]);

  if (isEditMode && isChannelMarkupLoading && !initialChannelMarkup) {
    return (
      <Container maxWidth="xl">
        <Alert severity="info">Loading channel markup...</Alert>
      </Container>
    );
  }

  if (isEditMode && isChannelMarkupFetchError && !initialChannelMarkup && !serverChannelMarkup) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Unable to load channel markup.</Alert>
      </Container>
    );
  }

  if (conditionTypeOptions.length === 0) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" variant="outlined">
          No creatable channel markup condition types are available. Check the contract configuration.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Channel Markup v2 | {isEditMode ? 'Edit' : 'Create'}</title>
      </Helmet>

      <PageSubheader title={isEditMode ? 'Edit Channel Markup v2' : 'Create Channel Markup v2'}>
        <Button variant="text" onClick={() => goBack()}>
          Back to list
        </Button>
      </PageSubheader>

      <Box component="form" onSubmit={handleCreate} mt={2} data-testid="channel-markup-v2-form">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
          <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
            {unsupportedConditionCount > 0 && (
              <Alert severity="warning" variant="outlined">
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">Unsupported condition rules detected</Typography>
                  <Typography variant="body2">
                    This markup includes {unsupportedConditionCount} condition rule
                    {unsupportedConditionCount === 1 ? '' : 's'} that the create/update schema cannot persist. Those
                    rules will be ignored on save, so the saved markup may behave differently from the loaded source
                    data. Remove or replace them before saving changes here.
                  </Typography>
                </Stack>
              </Alert>
            )}
            {saveWarnings.length > 0 && (
              <Alert severity="warning" variant="outlined">
                <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2 }}>
                  {saveWarnings.map((warning, index) => (
                    <li key={`${warning}-${index}`}>
                      <Typography variant="body2">{warning}</Typography>
                    </li>
                  ))}
                </Stack>
              </Alert>
            )}

            {!canSave && !isChecking && (
              <Alert severity="warning" variant="outlined">
                <Stack spacing={1}>
                  <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2 }}>
                    {checkWarnings.length > 0 ? (
                      checkWarnings.map((warning, index) => (
                        <li key={`${warning}-${index}`}>
                          <Typography variant="body2">{warning}</Typography>
                        </li>
                      ))
                    ) : (
                      <li>
                        <Typography variant="body2">
                          Channel markup cannot be saved yet. Refresh the check after making changes.
                        </Typography>
                      </li>
                    )}
                  </Stack>
                  <Box>
                    <Button variant="text" onClick={handleRefreshCheck} disabled={isChecking || isSubmitting}>
                      Refresh check
                    </Button>
                  </Box>
                </Stack>
              </Alert>
            )}

            {showDetails && (
              <Alert severity="info" variant="outlined">
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">Channel markup details</Typography>
                  <Typography variant="body2">
                    Markups control the actual pricing adjustment. Conditions decide when a markup applies. Edit a row
                    to see field-level guidance.
                  </Typography>
                </Stack>
              </Alert>
            )}

            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack spacing={0.5}>
                  <Typography variant="h6">Markups</Typography>
                  {showDetails && (
                    <Typography variant="body2" color="text.secondary">
                      Markups define how much the normal price or Lux Plus price is adjusted. They include the currency
                      and optional Call To Action text. This text supports replacement logic via templated
                      post-calculation fields
                    </Typography>
                  )}
                </Stack>
              </Stack>
              <DataGrid
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                columnHeaderHeight={showDetails ? 72 : undefined}
                getRowHeight={() => 'auto'}
                rows={markups}
                columns={markupGridWithActions}
                columnVisibilityModel={{ debug: false }}
                editMode="row"
                rowModesModel={markupRowModesModel}
                onRowModesModelChange={setMarkupRowModesModel}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={(updatedRow) => {
                  setMarkups((current) =>
                    current.map((row) => (row.id === updatedRow.id ? { ...row, ...updatedRow } : row)),
                  );
                  setHasUnsavedChanges(true);
                  markMarkupSaved(updatedRow.id);
                  return updatedRow;
                }}
                onProcessRowUpdateError={(error) => {
                  enqueueSnackbar(`Failed to update markup row: ${error}`, { variant: 'error' });
                }}
                sx={{
                  '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 },
                  '& .MuiDataGrid-row': { maxHeight: 'none !important' },
                }}
              />
              <Button
                startIcon={<AddIcon />}
                variant="text"
                onClick={() => {
                  const newMarkup = defaultMarkup();
                  setMarkups((current) => current.concat(newMarkup));
                  setMarkupRowModesModel((current) => ({
                    ...current,
                    [newMarkup.id]: { mode: GridRowModes.Edit, fieldToFocus: 'markup_type' },
                  }));
                  setHasUnsavedChanges(true);
                }}
              >
                Add markup
              </Button>
            </Stack>

            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Conditions</Typography>
              </Stack>
              <DataGrid
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                columnHeaderHeight={showDetails ? 72 : undefined}
                getRowHeight={() => 'auto'}
                rows={conditions}
                columns={conditionGridWithActions}
                columnVisibilityModel={{ debug: false }}
                editMode="row"
                rowModesModel={conditionRowModesModel}
                onRowModesModelChange={setConditionRowModesModel}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={(updatedRow) => {
                  setConditions((current) =>
                    current.map((row) => (row.id === updatedRow.id ? { ...row, ...updatedRow } : row)),
                  );
                  setHasUnsavedChanges(true);
                  markConditionSaved(updatedRow.id);
                  return updatedRow;
                }}
                onProcessRowUpdateError={(error) => {
                  enqueueSnackbar(`Failed to update condition row: ${error}`, { variant: 'error' });
                }}
                sx={{
                  '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 },
                  '& .MuiDataGrid-row': { maxHeight: 'none !important' },
                }}
              />
              <Button
                startIcon={<AddIcon />}
                variant="text"
                onClick={() => {
                  const newCondition = createDefaultCondition();
                  if (!newCondition) {
                    return;
                  }

                  setConditions((current) => current.concat(newCondition));
                  setConditionRowModesModel((current) => ({
                    ...current,
                    [newCondition.id]: { mode: GridRowModes.Edit, fieldToFocus: 'condition_type' },
                  }));
                  setHasUnsavedChanges(true);
                }}
              >
                Add condition
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              width: { xs: '100%', md: 320 },
              flexShrink: 0,
              position: { md: 'sticky' },
              top: { md: 24 },
            }}
          >
            <Stack
              spacing={2}
              sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}
            >
              <Typography variant="subtitle1">Summary</Typography>
              <TextField
                label="Brand"
                value={tenantBrand}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                helperText="Derived from the current tenant"
              />
              {displayedChannelMarkup && (
                <Stack spacing={0.5} aria-label="Channel markup record details">
                  <Typography variant="subtitle2">Record details</Typography>
                  <Typography variant="body2">ID: {displayedChannelMarkup.channelMarkupId}</Typography>
                  <Typography variant="body2">Version: {displayedChannelMarkup.version}</Typography>
                  <Typography variant="body2">
                    Created: {displayedChannelMarkup.createdAt ?? displayedChannelMarkup.created_at ?? '--'}
                  </Typography>
                  <Typography variant="body2">Warnings: {displayedChannelMarkup.warnings?.length ?? 0}</Typography>
                  {displayedChannelMarkup.warnings?.map((warning) => (
                    <Typography key={`${warning.code}-${warning.message}`} variant="body2" color="text.secondary">
                      {warning.level}: {warning.message}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          </Box>
        </Stack>

        <TextField
          label="Expires At"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          value={expiresAt}
          onChange={(event) => {
            setExpiresAt(event.target.value);
            setHasUnsavedChanges(true);
          }}
          helperText="Optional expiry for the channel markup."
        />

        <DialogActions sx={{ px: 0, pt: 3 }}>
          <Button startIcon={<InfoOutlinedIcon />} variant="text" onClick={() => setShowDetails((current) => !current)}>
            {showDetails ? 'Hide details' : 'More details'}
          </Button>
          <Button onClick={() => goBack()}>Cancel</Button>
          {hasUnsavedChanges && (
            <Typography variant="body2" color="warning.main" sx={{ alignSelf: 'center', fontWeight: 600 }}>
              You have unsaved changes
            </Typography>
          )}
          {canEditMarkup && (
            <Box data-testid="channel-markup-v2-submit-button">
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting || !canEditRows || isChecking || !canSave || unsupportedConditionCount > 0}
              >
                {isChecking ? 'Checking...' : isEditMode ? 'Update markup' : 'Create markup'}
              </Button>
            </Box>
          )}
        </DialogActions>

        <Alert severity="warning" variant="outlined">
          This is in active development - see channel markup v1 for current tested functionality
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <DebugModal type="generic" title="Channel Markup Debug" data={debugState} />
        </Box>
      </Box>

      <Box mt={2}>
        <ChannelMarkupTesterPanel
          title="Channel markup tester"
          description="Test the current markup rules against a simulated offer and user context."
          summaryId="channel-markup-tester-header"
          contentId="channel-markup-tester-content"
          showActiveMarkups
          activeChannelMarkups={activeChannelMarkups}
          isActiveChannelMarkupsLoading={isActiveChannelMarkupsLoading}
          testerMarkupMode={testerMarkupMode}
          setTesterMarkupMode={setTesterMarkupMode}
          onRunPreview={handleRunPreview}
          selectedCurrencies={selectedCurrencies}
          setSelectedCurrencies={setSelectedCurrencies}
          isClientInitReady={isClientInitReady}
          setTesterCheckVersion={setTesterCheckVersion}
          clientInitUpdatedAt={clientInitUpdatedAt}
          clientInitRefreshMessage={clientInitRefreshMessage}
          clientInitRefreshError={clientInitRefreshError}
          clientInitConfig={clientInitConfig}
          isClientInitFetching={isClientInitFetching}
          clientInitMode={clientInitMode}
          setClientInitMode={setClientInitMode}
          tenantBrand={tenantBrand}
          handleRefreshClientInit={handleRefreshClientInit}
          testerResults={testerResults}
          testerResult={testerResult}
          testerValues={testerValues}
          effectiveTesterContext={effectiveTesterContext}
          registerTester={registerTester}
          setTesterValue={setTesterValue}
          subscriberTierOptions={subscriberTierOptions}
          userRoleOptions={userRoleOptions}
          currencyOptions={currencyOptions}
          audienceSearchInput={audienceSearchInput}
          setAudienceSearchInput={setAudienceSearchInput}
          audienceSearchEnabled={audienceSearchEnabled}
          audienceOptions={audienceOptions}
          isAudienceSearchLoading={isAudienceSearchLoading}
          noAudienceResults={noAudienceResults}
        />
      </Box>
    </Container>
  );
}
